import { useState } from 'react';
import {
  Page,
  Card,
  InlineGrid,
  Text,
  Button,
  BlockStack,
  Toast,
  Frame,
  Banner,
  ProgressBar,
  InlineStack,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { client } from '../api/client';
import { Endpoints } from '../api/endpoints';

interface PlanConfig {
  price: number;
  price_yearly?: number;
  orders_per_month: number | null;
  products_with_cost: number | null;
  ad_platforms: number | null;
  forecast_days: number;
  stores: number;
  data_retention_days: number | null;
}

interface UsageMetric {
  used: number;
  limit: number | null;
}

interface Usage {
  orders: UsageMetric;
  products_with_cost: UsageMetric;
  ad_platforms: UsageMetric;
}

interface PlansResponse {
  current_plan: string;
  plans: Record<string, PlanConfig>;
  usage: Usage;
  // Managed Pricing (app live): Shopify hosts the plan picker and rejects the Billing API,
  // so the buttons link out instead of creating a charge ourselves.
  managed_pricing?: boolean;
  trial_days?: number;
  pricing_url?: string;
  // Test charge: the merchant approves Shopify's real confirmation screen but is never billed.
  test_charge?: boolean;
}

// Leave the embedded iframe — App Bridge intercepts `_top` and navigates the admin itself.
function openTopLevel(url: string) {
  if (!window.open(url, '_top')) (window.top ?? window).location.href = url;
}

const ORDER = ['free', 'starter', 'pro'];

function UsageRow({ label, metric }: { label: string; metric?: UsageMetric }) {
  if (!metric) return null;
  const lim = metric.limit;
  const unlimited = lim === null;
  const pct = lim === null || lim === 0 ? 0 : Math.min(100, (metric.used / lim) * 100);
  const tone: 'success' | 'highlight' | 'critical' =
    pct >= 90 ? 'critical' : pct >= 70 ? 'highlight' : 'success';

  return (
    <BlockStack gap="100">
      <InlineStack align="space-between">
        <Text as="span" variant="bodySm">
          {label}
        </Text>
        <Text as="span" variant="bodySm" tone="subdued">
          {metric.used}
          {unlimited ? ' (unlimited)' : ` / ${metric.limit}`}
        </Text>
      </InlineStack>
      {!unlimited && <ProgressBar progress={pct} tone={tone} size="small" />}
    </BlockStack>
  );
}

export default function Billing() {
  const { data, refetch } = useApi<PlansResponse>(Endpoints.billing.plans);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const subscribe = async (plan: string) => {
    // Managed Pricing: nothing to create — Shopify's own page owns the plan change.
    if (data?.managed_pricing && data.pricing_url) {
      openTopLevel(data.pricing_url);
      return;
    }

    setBusy(plan);
    try {
      const res = await client.post<{
        confirmation_url: string | null;
        pricing_url: string | null;
      }>(Endpoints.billing.subscribe, { plan });

      if (res.data.confirmation_url) {
        // Shopify's charge confirmation screen — must be top-level, not inside the iframe.
        openTopLevel(res.data.confirmation_url);
      } else if (res.data.pricing_url) {
        openTopLevel(res.data.pricing_url);
      } else {
        setToast(`Switched to ${plan} plan`);
        refetch();
      }
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const fmt = (v: number | null) => (v === null ? 'Unlimited' : String(v));
  const history = (days: number | null) =>
    days === null ? 'Unlimited' : days >= 365 ? `${Math.round(days / 365)} year` : `${days} days`;

  return (
    <Frame>
      <Page title="Billing" subtitle="Choose the plan that fits your store">
        <BlockStack gap="400">
          {data?.test_charge && !data?.managed_pricing && (
            <Banner tone="info" title="Test billing">
              <p>
                Charges are created in Shopify test mode. You will go through the real approval
                screen, but no money is collected and no invoice is issued.
              </p>
            </Banner>
          )}

          {data?.usage && (
            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingMd">
                  Current usage
                </Text>
                <UsageRow label="Orders tracked this month" metric={data.usage.orders} />
                <UsageRow label="Products with cost" metric={data.usage.products_with_cost} />
              </BlockStack>
            </Card>
          )}

          <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
          {ORDER.map((planKey) => {
            const plan = data?.plans?.[planKey];
            const isCurrent = data?.current_plan === planKey;
            if (!plan) return null;
            return (
              <Card key={planKey}>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingMd">
                    {planKey.charAt(0).toUpperCase() + planKey.slice(1)}
                  </Text>
                  <Text as="p" variant="heading2xl">
                    ${plan.price}
                    <Text as="span" variant="bodySm" tone="subdued">
                      {' '}
                      /mo
                    </Text>
                  </Text>
                  {plan.price_yearly ? (
                    <Text as="p" variant="bodySm" tone="subdued">
                      or ${plan.price_yearly}/year · {data?.trial_days ?? 14}-day free trial
                    </Text>
                  ) : null}
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm">
                      Orders: {fmt(plan.orders_per_month)}/mo
                    </Text>
                    <Text as="span" variant="bodySm">
                      Products with cost: {fmt(plan.products_with_cost)}
                    </Text>
                    {/* Ad platforms, ShipStation, Amazon and extra stores are in the plan config but
                        their integrations are switched off (routes/api.php), so they are not sold. */}
                    <Text as="span" variant="bodySm">
                      Profit forecast: {plan.forecast_days ? `${plan.forecast_days} days` : '—'}
                    </Text>
                    <Text as="span" variant="bodySm">
                      Order history: {history(plan.data_retention_days)}
                    </Text>
                  </BlockStack>
                  {isCurrent ? (
                    <Button variant="primary" tone="success" fullWidth>
                      Current plan
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={() => subscribe(planKey)}
                      loading={busy === planKey}
                    >
                      {data?.managed_pricing
                        ? 'Manage plan'
                        : planKey === 'free'
                          ? 'Downgrade'
                          : 'Upgrade'}
                    </Button>
                  )}
                </BlockStack>
              </Card>
            );
          })}
          </InlineGrid>
        </BlockStack>
      </Page>
      {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
    </Frame>
  );
}
